import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import { CText, CHeader, CInput, CButton } from '../../components/common';
import { FiCamera } from 'react-icons/fi';
import routes from '../../utils/routes';

const PersonalDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    profileHeadline: '',
    companyName: '',
    sebiQualification: '',
    sebiRegNumber: '',
    yearsOfExperience: '',
    languages: '',
    isCompanyAssociated: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: (user as any).phone || '',
        bio: (user as any).bio || '',
        profileHeadline: (user as any).profile_headline || '',
        companyName: (user as any).company_name || '',
        sebiQualification: (user as any).sebi_qualification || '',
        sebiRegNumber: (user as any).sebi_reg_number || '',
        yearsOfExperience: (user as any).years_of_xp?.toString() || '',
        languages: (user as any).languages?.join(', ') || '',
        isCompanyAssociated: (user as any).company_associated || false,
      });
    }
  }, [user]);

  const isExpert = (user as any)?.is_expert || false;

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update user in Redux
      const updatedUser = {
        ...user,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        bio: formData.bio,
        profile_headline: formData.profileHeadline,
        company_name: formData.companyName,
        sebi_qualification: formData.sebiQualification,
        years_of_xp: parseInt(formData.yearsOfExperience) || 0,
        languages: formData.languages.split(',').map((l) => l.trim()).filter(Boolean),
        company_associated: formData.isCompanyAssociated,
      };

      dispatch(setUser(updatedUser as any));
      navigate(routes.profile);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const sebiQualifications = [
    'Investment Advisor',
    'Research Analyst',
    'Portfolio Manager',
    'Stockbroker',
    'Merchant Banker',
  ];

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-3xl lg:mx-auto">
        <CHeader title="Personal Details" />

        {/* Profile Picture */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              {formData.name.charAt(0).toUpperCase() || 'U'}
            </div>
            <button
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
            >
              {React.createElement(FiCamera as any, { size: 16, color: colors.white })}
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="space-y-4">
          <CInput
            label="Full Name"
            value={formData.name}
            onChange={(val) => handleChange('name', val)}
            placeholder="Enter your full name"
            errorText={errors.name}
            required
          />

          <CInput
            label="Email"
            value={formData.email}
            onChange={(val) => handleChange('email', val)}
            placeholder="Enter your email"
            type="email"
            errorText={errors.email}
            required
          />

          <CInput
            label="Phone Number"
            value={formData.phone}
            onChange={(val) => handleChange('phone', val)}
            placeholder="Enter your phone number"
            type="tel"
          />
        </div>

        {/* Expert-specific fields */}
        {isExpert && (
          <>
            <div className="mt-8 mb-4">
              <CText type="B16" color={colors.textColor}>
                Expert Information
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                This information is visible to users
              </CText>
            </div>

            <div className="space-y-4">
              {/* Company Association */}
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => handleChange('isCompanyAssociated', !formData.isCompanyAssociated)}
                  className="w-5 h-5 rounded border-2 flex items-center justify-center"
                  style={{
                    borderColor: formData.isCompanyAssociated ? colors.primary : colors.grayScale500,
                    backgroundColor: formData.isCompanyAssociated ? colors.primary : 'transparent',
                  }}
                >
                  {formData.isCompanyAssociated && (
                    <span className="text-white text-xs">✓</span>
                  )}
                </button>
                <CText type="r14" color={colors.textColor}>
                  Associated with a company
                </CText>
              </div>

              {formData.isCompanyAssociated && (
                <CInput
                  label="Company Name"
                  value={formData.companyName}
                  onChange={(val) => handleChange('companyName', val)}
                  placeholder="Enter company name"
                />
              )}

              <CInput
                label="Profile Headline"
                value={formData.profileHeadline}
                onChange={(val) => handleChange('profileHeadline', val)}
                placeholder="e.g., Senior Investment Advisor"
              />

              <CInput
                label="Bio / Description"
                value={formData.bio}
                onChange={(val) => handleChange('bio', val)}
                placeholder="Tell users about yourself and your expertise"
                multiline
              />

              {/* SEBI Qualification Dropdown */}
              <div className="mb-3">
                <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 4 }}>
                  SEBI Qualification *
                </CText>
                <select
                  value={formData.sebiQualification}
                  onChange={(e) => handleChange('sebiQualification', e.target.value)}
                  className="w-full lg:max-w-lg p-3 rounded-xl border text-base"
                  style={{
                    backgroundColor: colors.inputBackground,
                    borderColor: colors.borderColor,
                    color: colors.textColor,
                  }}
                >
                  <option value="">Select qualification</option>
                  {sebiQualifications.map((qual) => (
                    <option key={qual} value={qual}>
                      {qual}
                    </option>
                  ))}
                </select>
              </div>

              <CInput
                label="SEBI Registration Number"
                value={formData.sebiRegNumber}
                onChange={(val) => handleChange('sebiRegNumber', val)}
                placeholder="INH000XXXXXX"
                editable={false}
              />

              <CInput
                label="Years of Experience"
                value={formData.yearsOfExperience}
                onChange={(val) => handleChange('yearsOfExperience', val)}
                placeholder="e.g., 10"
                type="number"
              />

              <CInput
                label="Languages (comma separated)"
                value={formData.languages}
                onChange={(val) => handleChange('languages', val)}
                placeholder="e.g., English, Hindi, Gujarati"
              />
            </div>

            {/* Expert Actions */}
            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.primary }}
              >
                <CText type="M14" color={colors.primary}>
                  Set Availability
                </CText>
              </button>
              <button
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.primary }}
              >
                <CText type="M14" color={colors.primary}>
                  Set Rates
                </CText>
              </button>
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="mt-8">
          <CButton
            title={loading ? 'Saving...' : 'Save Changes'}
            onPress={handleSave}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
